import Cookies from 'js-cookie';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';

import { cookieParams } from '../api/Auth';

import { getState } from './App';
import { getSetting } from './Settings';

export const getCurrentUser = () => {
  const state = getState();
  if (state && state.Auth.profile) {
    return state.Auth.profile;
  }

  return null;
};

export const getOriginalProfile = () => {
  const state = getState();
  if (state && state.Auth.originalProfile) {
    return state.Auth.originalProfile;
  }

  return null;
};

export const getShare = () => {
  const state = getState();
  if (state && state.Shares.share) {
    return state.Shares.share;
  }

  return null;
};

export const userIsSuperAdmin = () => {
  const user = getCurrentUser();

  return user && user.is_superadmin;
};

export const userIsAdmin = () => {
  const user = getCurrentUser();

  return user && user.is_admin;
};

export const userIsManager = () => {
  const user = getCurrentUser();

  return user && user.is_manager;
};

export const userIsWorkspaceOwner = () => {
  const state = getState();
  const user = getCurrentUser();

  if (!state.Auth.profile?.client) return false;

  return state.Auth.profile.client.workspace_owner?.uuid === user.uuid;
};

export const userCanSeeComments = () => {
  const user = getCurrentUser();

  return user && user.can_see_comments;
};

export const userCanSeeLikes = () => {
  const user = getCurrentUser();

  return user && user.can_see_likes;
};

export const userHasAccessToken = () => (
  Cookies.get('sp_access_token')
);

export const userHasCap = (cap, object) => {
  const user = getCurrentUser();

  if (!object) {
    if (!user || !user.capabilities) {
      return false;
    }

    if (user.capabilities.includes('all')) {
      if (['request_download_files_and_collections'].includes(cap)) {
        return false;
      }
      if (['reason_download_files_and_collections'].includes(cap)) {
        return false;
      }
      return true;
    }

    if ([
      'upload_file',
      'browse_archived_files',
      'request_download_files_and_collections',
      'reason_download_files_and_collections',
      'create_public_file',
      'create_public_collection',
      'share_download_requests',
      'manage_settings',
      'ftp',
      'upload_pages',
      'reports',
      'public_file_urls',
      'share_manage_settings',
      'share',
      'integrations',
      'my_collections',
      'create_collection_all',
      'collaborate_collections',
      'use_image_editor',
      'image_editor_save_versions',
      'add_users',
      'delete_files_collections_permanent',
      'edit_people',
    ].includes(cap)) {
      if (user.capabilities.includes(cap)) {
        return true;
      }
    }

    return false;
  }

  if (!object.capabilities) {
    return false;
  }

  if (object.capabilities.includes('all')) {
    if (
      ['request_download_files_and_collections'].includes(cap) &&
      !object.capabilities.includes('request_download_files_and_collections')
    ) {
      return false;
    }
    if (cap === 'download_files_and_collections' && typeof object.smart !== 'undefined' && !object.capabilities.includes(cap)) {
      return false;
    }
    if (cap === 'request_download_files_and_collections' && !object.capabilities.includes(cap)) {
      return false;
    }
    return true;
  }

  return object.capabilities.includes(cap);
};

export const userCan = (cap, object) => {
  const user = getCurrentUser();

  if (!user) {
    if (cap.indexOf('comment.') >= 0) {
      const share = getShare();
      if (!share) {
        return false;
      }
      switch (cap) {
        case 'comment.edit':
        case 'comment.delete': {
          if (object.user && share.shared_user && share.shared_user.uuid === object.user.uuid) {
            return true;
          }
          if (object.email && share.shared_email && share.shared_email === object.email) {
            return true;
          }
          return false;
        }
        default:
          return false;
      }
    } else {
      return false;
    }
  }

  if (['file.download', 'file.request_download'].includes(cap) && object) {
    if (object.type === 'link') {
      return false;
    }
  }

  if (user.is_superadmin || user.is_admin || user.is_manager || userHasCap('manage_settings')) {
    if (['person.edit'].includes(cap)) {
      return true;
    }
  }

  if (user.is_superadmin) {
    if (['collection.change_owner', 'file.change_owner'].includes(cap)) {
      return true;
    }
  }

  if (user.is_admin) {
    if (['collection.change_owner', 'file.change_owner'].includes(cap)) {
      return !getSetting('disable_change_owner');
    }

    if (['file.request_download', 'collection.request_download'].includes(cap)) {
      return false;
    }

    if (cap === 'desktop_sync') {
      return getSetting('desktop_sync_enabled');
    }

    return true;
  }

  switch (cap) {
    case 'collection.create':
      if (userHasCap('my_collections') || userHasCap('create_collection_all')) {
        return true;
      }
      break;
    case 'collection.create_public':
      if (userHasCap('create_public_collection', object)) {
        return true;
      }
      break;
    case 'collection.download':
      if (userHasCap('download_files_and_collections', object)) {
        return true;
      }
      break;
    case 'collection.request_download':
      if (userHasCap('request_download_files_and_collections', object)) {
        return true;
      }
      break;
    case 'collection.share':
      if (userHasCap('share', object)) {
        return true;
      }
      break;
    case 'collection.edit':
      if (user.uuid === object.user?.uuid || userHasCap('edit_collection', object)) {
        return true;
      }
      break;
    case 'collection.move':
      if (
        (object.public && getSetting('public_collections_open')) ||
        (!object.public && userCan('collection.edit', object))
      ) {
        return true;
      }
      break;
    case 'collection.delete':
      if (user.uuid === object.user?.uuid || userHasCap('delete_files_and_collections', object)) {
        return true;
      }
      break;
    case 'collection.add':
    case 'collection.remove':
      if ((object.user && user.uuid === object.user?.uuid) || object.allow_changes) {
        return true;
      }
      break;
    case 'collection.reorder':
      if (
        (user.role === null && userHasCap('edit_collection', object) && !object?.public) ||
        (user.role !== null && userHasCap('reorder_collection', object))
      ) {
        return true;
      }
      break;
    case 'collection.change_access':
      if (user.uuid === object.user?.uuid || userHasCap('edit_collection_access', object)) {
        return true;
      }
      break;
    case 'collection.actions':
      return true; // for subscribing / unsubscribing

    case 'file.actions':
      if (userHasCap('file_actions', object)) {
        return true;
      }
      break;
    case 'file.upload':
      if (userHasCap('upload_file')) {
        return true;
      }
      break;
    case 'file.create_public':
      if (userHasCap('create_public_file', object)) {
        return true;
      }
      break;
    case 'file.download':
      if ((object.user && user.uuid === object.user.uuid) || userHasCap('download_files_and_collections', object)) {
        return true;
      }
      break;
    case 'file.request_download':
      if (userHasCap('request_download_files_and_collections', object)) {
        return true;
      }
      break;
    case 'file.use_image_editor':
      if (userHasCap('use_image_editor', object)) {
        return true;
      }
      break;
    case 'file.share':
      if (userHasCap('share', object)) {
        return true;
      }
      break;
    case 'file.edit':
      if ((object.user && user.uuid === object.user.uuid) || userHasCap('edit_file', object)) {
        return true;
      }
      break;
    case 'file.delete':
      if ((object.user && user.uuid === object.user.uuid) || userHasCap('delete_files_and_collections', object)) {
        return true;
      }
      break;
    case 'file.browse_archived':
      if (userHasCap('browse_archived_files')) {
        return true;
      }
      break;
    case 'file.change_access':
      if ((object.user && user.uuid === object.user.uuid) || userHasCap('edit_file_access', object)) {
        return true;
      }
      break;
    case 'file.history':
      if (object.user && user.uuid === object.user.uuid) {
        return true;
      }
      break;

    case 'comment.edit':
    case 'comment.delete': {
      if (object.user && user.uuid === object.user.uuid) {
        return true;
      }
      break;
    }

    case 'share_download_requests':
      if (userHasCap('share_download_requests')) {
        return true;
      }
      break;

    case 'manage_settings':
      if (userHasCap('manage_settings')) {
        return true;
      }
      break;

    case 'edit_role': {
      if (userCan('manage_settings') && user.role && user.role.id !== object.id) {
        return true;
      }
      break;
    }

    case 'integrations': {
      if (userHasCap('integrations')) {
        return true;
      }
      break;
    }

    case 'share_manage_settings': {
      if (userHasCap('share_manage_settings')) {
        return true;
      }
      break;
    }

    case 'share':
      if (userHasCap('share', object)) {
        return true;
      }
      break;

    case 'add_users':
      if (userHasCap('add_users') || userIsAdmin() || userIsManager()) {
        return true;
      }
      break;

    case 'add_files_to_collections':
      if (
        userHasCap('collaborate_collections') ||
        userHasCap('my_collections') ||
        userHasCap('create_collection_all')
      ) {
        return true;
      }
      break;

    case 'use_collections':
      if (
        userHasCap('my_collections') ||
        userHasCap('create_collection_all')
      ) {
        return true;
      }
      break;

    default:
      return false;
  }

  return false;
};

export const userIsFileOwner = (file) => {
  const state = getState();

  const loggedInProfile = state.Auth.profile;
  return file && loggedInProfile && file.user.uuid === loggedInProfile.uuid;
};

export const userCanSeeVersions = (file) => (
  (userIsFileOwner(file) || (userCan('file.edit', file) && userHasCap('image_editor_save_versions'))) &&
  file.versions &&
  (!(file.parent || file.parent_uuid) || file.can_view_parent)
);

export function filterExcludingCapabilities(cap, capabilities) {
  let newCapabilities = capabilities;

  if (cap === 'reason_download_files_and_collections') {
    newCapabilities = newCapabilities.filter(c => (
      c !== 'download_files_and_collections' &&
      c !== 'request_download_files_and_collections'
    ));
  }
  if (cap === 'request_download_files_and_collections') {
    newCapabilities = newCapabilities.filter(c => (
      c !== 'download_files_and_collections' &&
      c !== 'reason_download_files_and_collections'
    ));
  }
  if (cap === 'download_files_and_collections') {
    newCapabilities = newCapabilities.filter(c => (
      c !== 'reason_download_files_and_collections' &&
      c !== 'request_download_files_and_collections'
    ));
  }
  if (cap === 'use_image_editor') {
    if (!newCapabilities.find((c => ([
      'download_files_and_collections',
      'reason_download_files_and_collections',
      'request_download_files_and_collections',
    ].includes(c))))) {
      newCapabilities = newCapabilities.filter(c => (c !== 'use_image_editor'));
    }
  }
  if (cap === 'delete_files_collections_permanent') {
    if (!newCapabilities.find((c => ([
      'delete_files_and_collections',
    ].includes(c))))) {
      newCapabilities = newCapabilities.filter(c => (c !== 'delete_files_collections_permanent'));
    }
  }

  return newCapabilities;
}

export function filterOutExcludingCapabilities(cap, capabilities) {
  let newCapabilities = capabilities;

  if (cap === 'download_files_and_collections') {
    newCapabilities = newCapabilities.filter(c => (c !== 'use_image_editor'));
  }

  return newCapabilities;
}

export const setLastActivity = () => {
  Cookies.set('sp_last_activity', moment().format(), cookieParams);
};

export const getLastActivity = () => {
  const lastActivity = Cookies.get('sp_last_activity');
  return lastActivity ? moment(lastActivity) : null;
};

export const getVisitorId = () => (
  Cookies.get('sp_vid')
);

export const setVisitorId = () => {
  let visitorId = getVisitorId();
  if (!visitorId) {
    visitorId = uuidv4();
  }
  Cookies.set('sp_vid', visitorId, { ...cookieParams, expires: 365 });
};

export const getVisitorNameFromCookie = () => (
  Cookies.get('sp_vname')
);

export const setVisitorNameCookie = (name) => {
  Cookies.set('sp_vname', name, { ...cookieParams, expires: 365 });
};

export const getUsedWorkspacesCookie = () => (
  Cookies.get('sp_workspaces')
);

export const getDomainWithoutSubdomain = (url) => {
  const urlObj = new URL(url);
  const urlParts = urlObj.hostname.split('.');
  return `${urlParts
    .slice(0)
    .slice(-(urlParts.length === 4 ? 3 : 2))
    .join('.')}${urlObj.port ? `:${urlObj.port}` : ''}`;
};

export const updateUsedWorkspacesCookie = () => {
  let cookie = getUsedWorkspacesCookie();
  if (cookie) {
    cookie = JSON.parse(atob(cookie));
  } else {
    cookie = {};
  }

  const workspacesCookieParams = {
    domain: getDomainWithoutSubdomain(window.location.origin),
    secure: window.location.protocol === 'https:',
    expires: 365,
  };

  cookie[window.location.hostname] = {
    color: `#${window.sp.color}`,
  };

  Cookies.set('sp_workspaces', btoa(JSON.stringify(cookie)), workspacesCookieParams);
};

export const getCapabilityAttribute = (cap) => {
  const user = getCurrentUser();

  if (!user || !user.capability_attributes) {
    return null;
  }

  return user.capability_attributes[cap] || null;
};

export const isRoleDisabled = (roleId) => {
  const allowedRoles = getCapabilityAttribute('add_users')?.roles || ['user'];
  return userCan('add_users') && !userCan('manage_settings') && !allowedRoles.includes(roleId) && !allowedRoles.includes(`${roleId}`);
};

export const isTeamDisabled = (teamUuid) => {
  const allowedTeams = getCapabilityAttribute('add_users')?.teams || [];
  return userCan('add_users') && !userCan('manage_settings') && !allowedTeams.includes(teamUuid);
};

export const decodeJwt = (token) => {
  const base64Payload = token.split('.')[1];
  const payload = Buffer.from(base64Payload, 'base64');
  return JSON.parse(payload.toString());
};
